import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Description as CertificateIcon
} from '@mui/icons-material';
import { awsDateToUS } from 'utils/dateUtils';

const CertificatePicker = ({ 
  open, 
  onClose, 
  onSelect, 
  certificates = [],
  selectedCertificateIds = [],
  loading = false,
  vendorName = ''
}) => {
  const [selected, setSelected] = useState(new Set(selectedCertificateIds));
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCertificates, setFilteredCertificates] = useState([]);

  useEffect(() => {
    // Filter certificates to only show REVIEW and APPROVED status
    const eligibleCerts = certificates.filter(cert => 
      cert.status === 'REVIEW' || cert.status === 'APPROVED'
    );

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      setFilteredCertificates(eligibleCerts.filter(cert => 
        (cert.insuredName?.toLowerCase().includes(searchLower)) ||
        (cert.insured?.toLowerCase().includes(searchLower)) ||
        (cert.certificateHolder?.toLowerCase().includes(searchLower)) ||
        (cert.id?.toLowerCase().includes(searchLower))
      ));
    } else {
      setFilteredCertificates(eligibleCerts);
    }
  }, [certificates, searchTerm]);

  const handleToggle = (certificateId) => {
    const newSelected = new Set(selected);
    if (newSelected.has(certificateId)) {
      newSelected.delete(certificateId);
    } else {
      newSelected.add(certificateId);
    }
    setSelected(newSelected);
  };

  const handleSelectAll = () => {
    if (selected.size === filteredCertificates.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredCertificates.map(cert => cert.id)));
    }
  };

  const handleConfirm = () => {
    onSelect(Array.from(selected));
    onClose();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'REVIEW':
        return 'default'; // Will use custom styling
      case 'EXPIRED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'APPROVED':
        return <CheckCircleIcon fontSize="small" />;
      case 'REVIEW':
        return <WarningIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const formatDate = (date) => {
    if (!date || date === '0000-01-01') return '-';
    try {
      return awsDateToUS(date);
    } catch {
      return '-';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '600px'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box>
          <Typography variant="h5" fontWeight="600">
            Select Certificates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose certificates to associate with this document request
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {/* Search Bar */}
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by insured name, certificate holder, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Results Summary */}
        <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {filteredCertificates.length} certificate{filteredCertificates.length !== 1 ? 's' : ''} available
            </Typography>
            {selected.size > 0 && (
              <Chip 
                label={`${selected.size} selected`} 
                color="primary" 
                size="small"
              />
            )}
          </Stack>
        </Box>

        {/* Certificates Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredCertificates.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CertificateIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {searchTerm 
                ? 'No certificates found matching your search'
                : 'No certificates available for selection (only Review and Approved certificates can be selected)'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selected.size > 0 && selected.size < filteredCertificates.length}
                      checked={filteredCertificates.length > 0 && selected.size === filteredCertificates.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Insured Name</TableCell>
                  <TableCell>Certificate Holder</TableCell>
                  <TableCell>Last Modified</TableCell>
                  <TableCell>Expiration Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCertificates.map((cert) => {
                  const isSelected = selected.has(cert.id);
                  return (
                    <TableRow
                      key={cert.id}
                      hover
                      onClick={() => handleToggle(cert.id)}
                      selected={isSelected}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={isSelected} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={cert.status}
                          color={getStatusColor(cert.status)}
                          size="small"
                          icon={getStatusIcon(cert.status)}
                          sx={cert.status === 'REVIEW' ? {
                            backgroundColor: 'rgba(128, 0, 128, 0.1)',
                            color: 'purple',
                            '& .MuiChip-icon': {
                              color: 'purple'
                            },
                            '&:hover': {
                              backgroundColor: 'rgba(128, 0, 128, 0.2)'
                            }
                          } : {}}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="500">
                          {cert.insuredName || cert.insured || '-'}
                        </Typography>
                        {vendorName && (
                          <Typography variant="caption" color="text.secondary">
                            {vendorName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {cert.certificateHolder || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(cert.updatedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(cert.firstExp)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {selected.size > 0 && (
          <Alert severity="info" sx={{ m: 2 }}>
            The vendor will be able to view these {selected.size} certificate{selected.size !== 1 ? 's' : ''} 
            in read-only mode when uploading the requested documents.
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        borderTop: '1px solid',
        borderColor: 'divider',
        px: 3,
        py: 2
      }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={selected.size === 0}
        >
          Confirm Selection ({selected.size})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CertificatePicker;